// poImportVendorGroups.ts — the vendor-triage panel's grouping algorithm.
//
// THE PROBLEM: normalized_name (computed server-side per document, exact
// legal-suffix-stripped equality) is precise but narrow. It correctly folds
// "Saturn Electronics" and "Saturn Electronics Corp" into one key, but
// "Amazon", "Amazon Business" and "Amazon Business Account" normalize to
// THREE DIFFERENT keys — none of them equal, so a naive group-by-key would
// show the reviewer three separate type-picks for what is, in practice, one
// vendor relationship. The spec calls this out by name: "near-duplicates
// grouped by normalized name (Amazon / Amazon Business / Amazon Business
// Account together)".
//
// THE FIX: union-find over CONTAINMENT, the same heuristic the backend
// already uses for its `suggestions` field (po-import-service.js
// _resolveVendor: "n.includes(key) || key.includes(n)", length >= 4). Applied
// here peer-to-peer within the batch's own new-vendor proposals, not just
// against already-seeded vendors. Two normalized keys merge when one contains
// the other and both are at least 4 characters (short keys like "tbd" or
// "na" would over-merge).
//
// A merged group may contain several distinct DISPLAY names (people typed
// "Amazon" on one PO and "Amazon Business" on another) — nameOptions lists
// them, most-frequent first, so the reviewer picks ONE name for the whole
// group instead of accepting whichever happened to parse first.

import type { VendorCreateProposal, VendorSuggestion } from './poImportApi';
import { normalizeVendorName } from './poImportApi';

export interface VendorGroupMember {
  normalizedKey: string;
  name: string;
  poCount: number;
  latestDate: string | null;
  isDisabledDefault: boolean;
  isDisabledReason: string | null;
  suggestions: VendorSuggestion[];
  docKeys: string[];
  proposal: VendorCreateProposal;
}

export interface VendorGroup {
  groupKey: string;
  members: VendorGroupMember[];
  nameOptions: { name: string; count: number }[];
  docKeys: string[];
  poCount: number;
  latestDate: string | null;
  suggestions: VendorSuggestion[];
  defaultDisabled: boolean;
  // The proposal fields (contact/address/etc) to seed a create with, taken
  // from whichever member is newest — same recency-wins rule the batch
  // harvest already used server-side.
  seedFields: VendorCreateProposal;
}

export interface VendorGroupInput {
  docKey: string;
  createProposal: VendorCreateProposal;
  suggestions: VendorSuggestion[];
}

const MIN_CONTAINMENT_LEN = 4;

/** Simple union-find over a fixed key set. */
function makeUnionFind(keys: string[]) {
  const parent = new Map<string, string>(keys.map((k) => [k, k]));
  const find = (k: string): string => {
    let root = k;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    // path compression
    let cur = k;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  return { find, union };
}

/**
 * Build vendor-triage groups from every staged document's create_proposal
 * (new-vendor proposals only — matched vendors never enter this pipeline).
 *
 * @param inputs one entry per document that has a create_proposal
 */
export function buildVendorGroups(inputs: VendorGroupInput[]): VendorGroup[] {
  const raw = new Map<string, VendorGroupMember>();

  for (const { docKey, createProposal: cp, suggestions } of inputs) {
    const key = cp.normalized_name || normalizeVendorName(cp.name);
    if (!raw.has(key)) {
      raw.set(key, {
        normalizedKey: key,
        name: cp.name,
        poCount: 0,
        latestDate: null,
        isDisabledDefault: cp.is_disabled,
        isDisabledReason: cp.is_disabled_reason,
        suggestions: [],
        docKeys: [],
        proposal: cp,
      });
    }
    const member = raw.get(key) as VendorGroupMember;
    member.poCount += cp.po_count_in_batch || 1;
    const newer = !!cp.latest_order_date && (!member.latestDate || cp.latest_order_date > member.latestDate);
    if (newer) {
      member.latestDate = cp.latest_order_date;
      member.proposal = cp; // recency wins for the seed fields, same as the server's harvest
    } else if (!member.latestDate) {
      member.latestDate = cp.latest_order_date;
    }
    // Not-disabled wins: if ANY document naming this key is recent, the
    // member itself is not stale by default.
    if (!cp.is_disabled) {
      member.isDisabledDefault = false;
      member.isDisabledReason = null;
    }
    for (const s of suggestions) {
      if (!member.suggestions.some((x) => x.vendor_id === s.vendor_id)) member.suggestions.push(s);
    }
    if (!member.docKeys.includes(docKey)) member.docKeys.push(docKey);
  }

  const keys = Array.from(raw.keys());
  const { find, union } = makeUnionFind(keys);
  for (let i = 0; i < keys.length; i += 1) {
    for (let j = i + 1; j < keys.length; j += 1) {
      const a = keys[i];
      const b = keys[j];
      if (a.length < MIN_CONTAINMENT_LEN || b.length < MIN_CONTAINMENT_LEN) continue;
      if (a.includes(b) || b.includes(a)) union(a, b);
    }
  }

  const byRoot = new Map<string, VendorGroupMember[]>();
  for (const key of keys) {
    const root = find(key);
    if (!byRoot.has(root)) byRoot.set(root, []);
    (byRoot.get(root) as VendorGroupMember[]).push(raw.get(key) as VendorGroupMember);
  }

  const groups: VendorGroup[] = [];
  for (const members of byRoot.values()) {
    const nameCounts = new Map<string, number>();
    for (const m of members) nameCounts.set(m.name, (nameCounts.get(m.name) || 0) + m.poCount);
    const nameOptions = Array.from(nameCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    const suggestions: VendorSuggestion[] = [];
    for (const m of members) {
      for (const s of m.suggestions) {
        if (!suggestions.some((x) => x.vendor_id === s.vendor_id)) suggestions.push(s);
      }
    }
    suggestions.sort((a, b) => b.overlap - a.overlap);

    let latestDate: string | null = null;
    let seedFields: VendorCreateProposal = members[0].proposal;
    for (const m of members) {
      if (m.latestDate && (!latestDate || m.latestDate > latestDate)) {
        latestDate = m.latestDate;
        seedFields = m.proposal;
      }
    }

    // The groupKey is a composite of every member's normalizedKey, sorted —
    // NOT the union-find root label, which depends on Map iteration order and
    // would silently reshuffle (and orphan any resolution the reviewer already
    // made) as soon as a new document joined the merge. A composite key is
    // stable for a given final merged SET regardless of processing order; it
    // only changes if the set itself changes (a new near-duplicate name joins
    // mid-review), which is the one case a key change is actually correct.
    const groupKey = members
      .map((m) => m.normalizedKey)
      .sort()
      .join('|');

    groups.push({
      groupKey,
      members,
      nameOptions,
      docKeys: members.flatMap((m) => m.docKeys),
      poCount: members.reduce((sum, m) => sum + m.poCount, 0),
      latestDate,
      suggestions,
      defaultDisabled: members.every((m) => m.isDisabledDefault),
      seedFields,
    });
  }

  // Biggest groups (most documents riding on one decision) surface first —
  // that is where one type-pick buys the most throughput.
  return groups.sort((a, b) => b.docKeys.length - a.docKeys.length || b.poCount - a.poCount);
}

/**
 * Client-side near-match suggestions for a vendor name that was typed by the
 * reviewer (a document with NO vendor name at all) rather than proposed by
 * the server. Same containment heuristic as the backend's _resolveVendor, run
 * against the vendor list already loaded for the "link to an existing vendor"
 * picker.
 */
export function suggestExistingVendors(
  name: string,
  vendors: { id: string; name: string }[]
): VendorSuggestion[] {
  const key = normalizeVendorName(name);
  if (key.length < MIN_CONTAINMENT_LEN) return [];
  const out: VendorSuggestion[] = [];
  for (const v of vendors) {
    const n = normalizeVendorName(v.name);
    if (n.length < MIN_CONTAINMENT_LEN) continue;
    if (n === key || n.includes(key) || key.includes(n)) {
      out.push({ vendor_id: v.id, name: v.name, overlap: Math.min(n.length, key.length) });
    }
  }
  out.sort((a, b) => b.overlap - a.overlap);
  return out.slice(0, 3);
}
